(() => {
  const validationMessages = {
    en: {
      name: 'Please enter a name between 2 and 100 characters.',
      email: 'Please enter a valid email address.',
      phone: 'Please enter a valid phone number.',
      date: 'Please choose a valid requested date.',
      pastDate: 'The requested date cannot be in the past.',
      groupSize: 'Group size must be a whole number between 1 and 200.',
      language: 'Please choose DE, EN or FR.',
      notes: 'Additional notes cannot exceed 1500 characters.',
      submit: 'Unable to send your request. Please try again.',
    },
    de: {
      name: 'Bitte geben Sie einen Namen mit 2 bis 100 Zeichen ein.',
      email: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
      phone: 'Bitte geben Sie eine gültige Telefonnummer ein.',
      date: 'Bitte wählen Sie ein gültiges Wunschdatum aus.',
      pastDate: 'Das Wunschdatum darf nicht in der Vergangenheit liegen.',
      groupSize: 'Die Gruppengröße muss eine ganze Zahl zwischen 1 und 200 sein.',
      language: 'Bitte wählen Sie DE, EN oder FR.',
      notes: 'Zusätzliche Hinweise dürfen maximal 1500 Zeichen lang sein.',
      submit: 'Ihre Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
    },
    fr: {
      name: 'Veuillez saisir un nom comprenant entre 2 et 100 caractères.',
      email: 'Veuillez saisir une adresse e-mail valide.',
      phone: 'Veuillez saisir un numéro de téléphone valide.',
      date: 'Veuillez sélectionner une date valide.',
      pastDate: 'La date demandée ne peut pas être antérieure à la date du jour.',
      groupSize: 'La taille du groupe doit être un nombre entier compris entre 1 et 200.',
      language: 'Veuillez sélectionner DE, EN ou FR.',
      notes: 'Les notes supplémentaires ne peuvent pas dépasser 1 500 caractères.',
      submit: "Impossible d’envoyer votre demande. Veuillez réessayer.",
    },
  };

  const getPageLanguage = () => document.documentElement.lang || window.Shopify?.locale || 'en';

  const getLocale = () => {
    const language = getPageLanguage().toLowerCase().split('-')[0];
    return Object.prototype.hasOwnProperty.call(validationMessages, language) ? language : 'en';
  };

  // Translated messages rendered by Liquid (locale files) take precedence over the built-in fallbacks.
  const getMessages = (section, locale) => {
    const fallbackMessages = { ...validationMessages.en, ...validationMessages[locale] };
    const messagesScript = section.querySelector('[data-guided-tour-messages]');
    if (!(messagesScript instanceof HTMLScriptElement)) return fallbackMessages;

    try {
      const translatedMessages = JSON.parse(messagesScript.textContent || '{}');
      Object.keys(fallbackMessages).forEach((key) => {
        const message = translatedMessages[key];
        if (typeof message === 'string' && message.trim() && !message.startsWith('translation missing')) {
          fallbackMessages[key] = message;
        }
      });
    } catch (_) {}

    return fallbackMessages;
  };

  const replaceSuccessTokens = (container, replacements) => {
    if (!(container instanceof HTMLElement)) return;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      let value = node.nodeValue || '';
      Object.entries(replacements).forEach(([token, replacement]) => {
        value = value.replaceAll(token, replacement);
      });
      node.nodeValue = value;
    });
  };

  const initBookingForm = (section) => {
    if (!(section instanceof HTMLElement) || section.dataset.tourBookingFormInitialized === 'true') return;

    const requestDrawer = section.querySelector('[data-guided-tour-drawer]');
    const requestDialogComponent = section.querySelector('[data-guided-tour-dialog-component]');
    const requestOpen = section.querySelector('[data-guided-tour-open]');
    const requestClose = section.querySelector('[data-guided-tour-close]');
    const requestForm = section.querySelector('.pp-tour-booking__request-form');
    const requestSubmitButton = section.querySelector('[data-guided-tour-submit]');
    const requestName = section.querySelector('[data-guided-tour-name]');
    const requestEmail = section.querySelector('[data-guided-tour-email]');
    const requestPhone = section.querySelector('[data-guided-tour-phone]');
    const requestDate = section.querySelector('[data-guided-tour-date]');
    const requestGroupSize = section.querySelector('[data-guided-tour-group-size]');
    const requestNotes = section.querySelector('[data-guided-tour-notes]');
    const requestInterestOptions = Array.from(section.querySelectorAll('[data-guided-tour-interest]'));
    const requestInterestsValue = section.querySelector('[data-guided-tour-interests-value]');
    const requestBody = section.querySelector('[data-guided-tour-body]');
    const requestSelects = Array.from(section.querySelectorAll('[data-guided-tour-select]'));
    const requestLanguage = requestSelects.find((select) => select instanceof HTMLSelectElement);
    const requestStatus = section.querySelector('[data-guided-tour-status]');
    const successDialog = section.querySelector('[data-guided-tour-success]');
    const successClose = section.querySelector('[data-guided-tour-success-close]');
    const successContent = section.querySelector('[data-guided-tour-success-content]');
    const successFallback = section.querySelector('[data-guided-tour-success-fallback]');
    const postedSuccessfully = section.querySelector('[data-guided-tour-posted-successfully]');
    const serverErrors = section.querySelector('[data-guided-tour-server-errors]');
    const bookingStorageKey = `pp-guided-tour-booking-${requestForm?.id || 'form'}`;
    const bookingReturnParameter = 'pp_booking_return';
    const bookingDateParameter = 'pp_booking_date';
    const bookingGroupParameter = 'pp_booking_group';
    const locale = getLocale();
    const messages = getMessages(section, locale);

    if (!(requestForm instanceof HTMLFormElement)) return;

    const closeRequestDrawer = () => {
      if (!(requestDrawer instanceof HTMLDialogElement) || !requestDrawer.open) return;
      if (typeof requestDialogComponent?.closeDialog === 'function') {
        requestDialogComponent.closeDialog();
      }
    };

    const openRequestDrawer = () => {
      if (!(requestDrawer instanceof HTMLDialogElement) || requestDrawer.open) return;
      customElements.whenDefined('dialog-component').then(() => {
        if (requestDrawer.open || typeof requestDialogComponent?.showDialog !== 'function') return;
        requestDialogComponent.showDialog();
        window.setTimeout(() => {
          requestDrawer.querySelector('input:not([type="hidden"])')?.focus();
        }, 250);
      });
    };

    requestOpen?.addEventListener('click', openRequestDrawer);
    requestClose?.addEventListener('click', closeRequestDrawer);

    if (requestDate instanceof HTMLInputElement) {
      const now = new Date();
      requestDate.min = new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
      const syncDateValue = () => {
        requestDate.closest('.pp-tour-booking__request-date')?.classList.toggle('has-value', Boolean(requestDate.value));
      };
      requestDate.addEventListener('input', syncDateValue);
      requestDate.addEventListener('change', syncDateValue);
      requestDate.addEventListener('click', () => {
        try {
          requestDate.showPicker?.();
        } catch (_) {}
      });
      syncDateValue();
    }

    requestSelects.forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      const syncSelectValue = () => select.classList.toggle('has-value', Boolean(select.value));
      select.addEventListener('change', syncSelectValue);
      syncSelectValue();
    });

    const closeSuccessDialog = () => {
      if (successDialog instanceof HTMLDialogElement && successDialog.open) successDialog.close();
    };
    const openDialog = (dialog) => {
      if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
      try {
        dialog.showModal();
      } catch (_) {
        dialog.setAttribute('open', '');
      }
    };
    successClose?.addEventListener('click', closeSuccessDialog);
    successDialog?.addEventListener('click', (event) => {
      if (event.target === successDialog) closeSuccessDialog();
    });

    const responseUrl = new URL(window.location.href);
    const isBookingReturn = responseUrl.searchParams.get(bookingReturnParameter) === 'success';

    if ((postedSuccessfully || isBookingReturn) && !serverErrors) {
      let submittedState = {};
      try {
        submittedState = JSON.parse(sessionStorage.getItem(bookingStorageKey) || '{}');
        sessionStorage.removeItem(bookingStorageKey);
      } catch (_) {}

      const submittedDate = submittedState.requestedDate || responseUrl.searchParams.get(bookingDateParameter) || '';
      const submittedGroupSize = submittedState.groupSize || responseUrl.searchParams.get(bookingGroupParameter) || '';

      let formattedDate = submittedDate || '-';
      if (submittedDate) {
        try {
          formattedDate = new Intl.DateTimeFormat(getPageLanguage(), { dateStyle: 'long' }).format(
            new Date(`${submittedDate}T00:00:00`),
          );
        } catch (_) {}
      }

      const requiredTokens = ['[tour_name]', '[requested_date]', '[group_size]'];
      const successMessage = successContent?.textContent || '';
      const hasRequiredStructure =
        successContent instanceof HTMLElement &&
        Boolean(successContent.querySelector('p')) &&
        Boolean(successContent.querySelector('ul')) &&
        successContent.querySelectorAll('li').length === 3 &&
        successContent.querySelectorAll('strong').length >= 2;
      if (
        successContent instanceof HTMLElement &&
        successFallback instanceof HTMLTemplateElement &&
        (!hasRequiredStructure || !requiredTokens.every((token) => successMessage.includes(token)))
      ) {
        successContent.replaceChildren(successFallback.content.cloneNode(true));
      }

      replaceSuccessTokens(successContent, {
        '[tour_name]': submittedState.tourName || successContent?.dataset.tourName || '-',
        '[requested_date]': formattedDate,
        '[group_size]': submittedGroupSize || '-',
      });

      responseUrl.searchParams.delete(bookingReturnParameter);
      responseUrl.searchParams.delete(bookingDateParameter);
      responseUrl.searchParams.delete(bookingGroupParameter);
      responseUrl.searchParams.delete('contact_posted');
      window.history.replaceState({}, '', `${responseUrl.pathname}${responseUrl.search}${responseUrl.hash}`);

      requestAnimationFrame(() => {
        openDialog(successDialog);
      });
    } else if (serverErrors) {
      if (requestStatus instanceof HTMLElement) {
        requestStatus.textContent = serverErrors.textContent?.trim() || requestStatus.dataset.errorMessage || messages.submit;
      }
      requestAnimationFrame(() => {
        openRequestDrawer();
      });
    }

    const invalidate = (field, message) => {
      field?.setAttribute('aria-invalid', 'true');
      if (requestStatus instanceof HTMLElement) requestStatus.textContent = message;
      field?.focus();
      return false;
    };

    const validateRequestForm = () => {
      requestForm.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));

      if (!(requestName instanceof HTMLInputElement)) return false;
      if (!(requestEmail instanceof HTMLInputElement)) return false;
      if (!(requestDate instanceof HTMLInputElement)) return false;

      requestName.value = requestName.value.trim().replace(/\s+/g, ' ');
      if (requestName.value.length < 2 || requestName.value.length > 100) {
        return invalidate(requestName, messages.name);
      }

      requestEmail.value = requestEmail.value.trim();
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      if (requestEmail.value.length > 254 || !emailPattern.test(requestEmail.value)) {
        return invalidate(requestEmail, messages.email);
      }

      if (requestPhone instanceof HTMLInputElement) {
        requestPhone.value = requestPhone.value.trim();
        const phoneDigits = requestPhone.value.replace(/\D/g, '');
        const phonePattern = /^[+()\d\s./-]+$/;
        if (requestPhone.value && (!phonePattern.test(requestPhone.value) || phoneDigits.length < 6 || phoneDigits.length > 15)) {
          return invalidate(requestPhone, messages.phone);
        }
      }

      const datePattern = /^\d{4}-\d{2}-\d{2}$/;
      const requestedDateValue = requestDate.value;
      const parsedDate = new Date(`${requestedDateValue}T00:00:00`);
      const [requestedYear, requestedMonth, requestedDay] = requestedDateValue.split('-').map(Number);
      const isExactDate =
        !Number.isNaN(parsedDate.getTime()) &&
        parsedDate.getFullYear() === requestedYear &&
        parsedDate.getMonth() + 1 === requestedMonth &&
        parsedDate.getDate() === requestedDay;
      if (!datePattern.test(requestedDateValue) || !isExactDate) {
        return invalidate(requestDate, messages.date);
      }
      if (requestDate.min && requestedDateValue < requestDate.min) {
        return invalidate(requestDate, messages.pastDate);
      }

      if (requestGroupSize instanceof HTMLInputElement && requestGroupSize.value) {
        const groupSize = Number(requestGroupSize.value);
        if (!Number.isInteger(groupSize) || groupSize < 1 || groupSize > 200) {
          return invalidate(requestGroupSize, messages.groupSize);
        }
      }

      if (
        requestLanguage instanceof HTMLSelectElement &&
        requestLanguage.value &&
        !['DE', 'EN', 'FR'].includes(requestLanguage.value)
      ) {
        return invalidate(requestLanguage, messages.language);
      }

      if (requestNotes instanceof HTMLTextAreaElement) {
        requestNotes.value = requestNotes.value.trim();
        if (requestNotes.value.length > 1500) {
          return invalidate(requestNotes, messages.notes);
        }
      }

      return true;
    };

    requestSubmitButton?.addEventListener('click', (event) => {
      if (requestStatus instanceof HTMLElement) requestStatus.textContent = '';
      if (!validateRequestForm()) {
        event.preventDefault();
      }
    });

    requestForm?.addEventListener('submit', (event) => {
      if (!(requestForm instanceof HTMLFormElement)) return;

      if (requestStatus instanceof HTMLElement) requestStatus.textContent = '';
      if (!validateRequestForm()) {
        event.preventDefault();
        return;
      }

      if (requestSubmitButton instanceof HTMLButtonElement) requestSubmitButton.disabled = true;

      const requestedDate = requestDate instanceof HTMLInputElement ? requestDate.value : '';
      const groupSize = requestGroupSize instanceof HTMLInputElement ? requestGroupSize.value : '';
      const tourField = requestForm.elements.namedItem('contact[Tour]');
      const tourName = tourField instanceof HTMLInputElement ? tourField.value : '';
      const selectedInterests = requestInterestOptions.filter(
        (option) => option instanceof HTMLInputElement && option.checked,
      );
      const selectedInterestValue = selectedInterests.map((option) => option.value).join(', ');

      if (requestInterestsValue instanceof HTMLInputElement) {
        requestInterestsValue.value = selectedInterestValue;
      }

      if (requestBody instanceof HTMLInputElement) {
        const tourUrlField = requestForm.elements.namedItem('contact[Tour URL]');
        requestBody.value = [
          `Tour: ${tourField instanceof HTMLInputElement ? tourField.value : ''}`,
          `Tour URL: ${tourUrlField instanceof HTMLInputElement ? tourUrlField.value : ''}`,
          `Tour interest: ${selectedInterestValue || '-'}`,
          `Requested date: ${requestedDate}`,
          `Group size: ${groupSize || '-'}`,
          `Language: ${requestLanguage instanceof HTMLSelectElement && requestLanguage.value ? requestLanguage.value : '-'}`,
          `Additional notes: ${requestNotes instanceof HTMLTextAreaElement && requestNotes.value ? requestNotes.value : '-'}`,
        ].join('\n');
      }

      try {
        sessionStorage.setItem(bookingStorageKey, JSON.stringify({ requestedDate, groupSize, tourName }));
      } catch (_) {
        // Shopify still receives the native form submission when browser storage is unavailable.
      }

      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.delete('contact_posted');
      returnUrl.searchParams.set(bookingReturnParameter, 'success');
      returnUrl.searchParams.set(bookingDateParameter, requestedDate);
      returnUrl.searchParams.set(bookingGroupParameter, groupSize);

      let returnToField = requestForm.elements.namedItem('return_to');
      if (!(returnToField instanceof HTMLInputElement)) {
        returnToField = document.createElement('input');
        returnToField.type = 'hidden';
        returnToField.name = 'return_to';
        requestForm.append(returnToField);
      }
      returnToField.value = `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
    });

    section.dataset.tourBookingFormInitialized = 'true';
  };

  const initBookingForms = (scope = document) => {
    const sections =
      scope instanceof Element && scope.matches('[data-pp-tour-booking]')
        ? [scope]
        : Array.from(scope.querySelectorAll('[data-pp-tour-booking]'));
    sections.forEach(initBookingForm);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initBookingForms(), { once: true });
  } else {
    initBookingForms();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) initBookingForms(event.target);
  });
})();
